/**
 * Aplicación web Directorio del Ayuntamiento de Zamora.
 * 
 * Desarrollo e implementación: Jaime León Pereira.
 * Ingeniero Informático de la Unidad TIC.
 */
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../styles/AdminLayout.css";
import { useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";
import jwtDecode from "jwt-decode";
import { exportCsvZip } from "../../services/exportService";

/**
 * Componente de barra lateral de administración.
 * 
 * Proporciona navegación entre las diferentes secciones de administración
 * y muestra información del usuario autenticado obtenida de Microsoft Entra.
 * Los permisos de acceso se determinan mediante roles JWT decodificados.
 */
const AdminSidebar = ({ activeSection, setActiveSection }) => {
  /* ============================
     Autenticación (MSAL)
  ============================ */
  
  // Hook de MSAL para acceder a la instancia, cuentas y estado de autenticación
  const { instance, accounts, inProgress } = useMsal();
	
  // Exportación de BBDD
  const [exporting, setExporting] = useState(false);

  /* ============================
     Estado del usuario
  ============================ */
  
  // Email del usuario obtenido del token
  const [userEmail, setUserEmail] = useState(null);
  // Roles del usuario extraídos del token JWT
  const [roles, setRoles] = useState([]);
  // Estado de carga durante la obtención de información del usuario
  const [loading, setLoading] = useState(true);

    /**
   * Efecto para cargar información del usuario al montar el componente
   * y cuando cambia el estado de autenticación de MSAL.
   * 
   * Obtiene el token de acceso de forma silenciosa, lo decodifica
   * y extrae el email y roles del usuario para control de permisos.
   */
  useEffect(() => {
    const loadUserInfo = async () => {
      setLoading(true);

      if (!accounts || accounts.length === 0) {
        setUserEmail(null);
        setRoles([]);
        setLoading(false);
        return;
      }

      try {
        // usa la variable de entorno definida en .env para el scope de la API
        const scope = process.env.REACT_APP_API_SCOPE;
        if (!scope) throw new Error("REACT_APP_API_SCOPE no definido en .env");

        // Obtener token de acceso de forma silenciosa (sin interacción del usuario)
        const resp = await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: [scope],
        });

        const accessToken = resp.accessToken;
        if (!accessToken) throw new Error("No viene accessToken en la respuesta MSAL");

        // Se decodifica el access token para leer claims (roles, aud, preferred_username...)
        const decoded = jwtDecode(accessToken);

        // Extraer email / username de los claim del del token
        const email =
          decoded.preferred_username ||
          decoded.upn ||
          decoded.unique_name ||
          accounts[0]?.username ||
          null;

        // Extraer roles del token (pueden venir en diferentes claims)
        const tokenRoles = decoded.roles || decoded.role || [];

        // Normalizar roles a array por si viene como string único
        const normalizedRoles = Array.isArray(tokenRoles) ? tokenRoles : [tokenRoles];

        setUserEmail(email);
        setRoles(normalizedRoles);
      } catch (err) {
        // Error controlado: no romper la aplicación, solo loguear y limpiar valores
        console.error("AdminSidebar: error obteniendo token/claims:", err);
        setUserEmail(null);
        setRoles([]);
      } finally {
        setLoading(false);
      }
    };

    // Sólo ejecutar cuando MSAL esté listo ("inProgress === "none")
    if (inProgress === "none") loadUserInfo();
    // re-ejecutar si cambia la cuenta o instancia de MSAL
  }, [instance, accounts, inProgress]);

/* ===============
     EXPORT ZIP 
   =============== */
  const handleExport = async () => {
    if (exporting) return;

    try {
      setExporting(true);

      const token = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [process.env.REACT_APP_API_SCOPE],
      });

      await exportCsvZip(token.accessToken);

    } finally {
      setExporting(false);
    }
  };

  /* ============================
     Lógica de permisos
  ============================ */
  
  // Determinar si el usuario tiene permisos de administrador
  // Se verifican diferentes formatos de roles por compatibilidad
  const isAdmin = roles.includes("ROLE_ADMIN") || roles.includes("ADMIN") || roles.includes("Admin");

  /* ============================
     Configuración del menú
  ============================ */
  
  // Items del menú de navegación
  const menuItems = [
    { id: "personas", label: "Personal", icon: "bi-person-fill" },
    { id: "departamentos", label: "Departamentos", icon: "bi-diagram-3-fill" },
    { id: "edificios", label: "Edificios", icon: "bi-building-fill" },
  ];

  /* ============================
     Estados de autenticación
  ============================ */

  // Si no hay cuentas (usuario no autenticado)
  if (!accounts || accounts.length === 0) {
    return <p>No autenticado</p>;
  }

  // Mostrar indicador de carga mientras se obtiene información del usuario
  if (loading) {
    return (
      <div className="admin-sidebar d-flex flex-column text-white shadow-lg p-3">
        <div>Cargando usuario...</div>
      </div>
    );
  }

  /*************************************************************
   * Renderizado de la barra lateral de administración         *
   *************************************************************/
  return (
     /* Contenedor principal de la barra lateral */
    <div
      className="admin-sidebar d-flex flex-column text-white shadow-lg"
      style={{
        width: "240px",
        minHeight: "100vh",
        background: "linear-gradient(180deg, #1f2937 0%, #111827 100%)",
      }}
    >
       {/* ======================================
          Encabezado con información del usuario
        ========================================= */}
      <div className="px-3 py-4 border-bottom border-secondary text-centre">
        {/* Título de la sección de administración */}
        <h5 className="fw-bold text-center mb-3">
          <i className="bi bi-speedometer2 me-2 text-primary"></i>
          Administrador
        </h5>

        {/* Información del usuario y badge de rol */}
        {isAdmin ? (
          <div className="text-center small text-secondary">
            <div>{userEmail || accounts[0]?.username}</div>
            <div className="badge bg-primary mt-1">
              {roles[0] ? roles[0].replace(/^ROLE_/, "") : "ADMIN"}
            </div>
          </div>
        ) : (
          <div className="text-center small text-secondary">
            <div>{userEmail || accounts[0]?.username}</div>
            <div className="badge bg-secondary mt-1">Usuario</div>
          </div>
        )}
      </div>

      {/* =============================
          Menú de navegación principal
          ============================= */}
      <ul className="nav flex-column px-2 mt-3 me-2">
        {menuItems.map((item) => {
          const isActive = activeSection === item.id;
          return (
            <li
              key={item.id}
              className={`nav-item mb-2 rounded-3 ${isActive ? "active-item" : ""}`}
            >
              <button
                className={`btn w-100 text-start d-flex align-items-center px-3 py-2 border-0 hover-bg ${
                  isActive ? "text-white fw-semibold" : "text-light"
                }`}
                onClick={() => setActiveSection(item.id)}
              >
                {/* Icono del item del menú */}
                <i
                  className={`bi ${item.icon} me-2 fs-5 ${
                    isActive ? "text-primary" : "text-secondary"
                  }`}
                ></i>
                {/* Etiqueta del item del menú */}
                {item.label}
              </button>
            </li>
          );
        })}
        {/* EXPORT */}
        <li className="nav-item mb-2 rounded-3">
          <button
            onClick={handleExport}
            disabled={exporting}
            className={`btn w-100 text-start d-flex align-items-center px-3 py-2 border-0 hover-bg ${
              exporting ? "active-item text-white fw-semibold opacity-75" : "text-light"
            }`}
          >
            <i
              className={`bi ${
                exporting ? "bi-arrow-repeat spin" : "bi-file-earmark-zip"
              } me-2 fs-5 ${
                exporting ? "text-primary" : "text-secondary"
              }`}
            />

            {exporting ? "Exportando..." : "Exportar datos (ZIP)"}
          </button>
        </li>

      </ul>
    </div>
  );
};

export default AdminSidebar;
